import { useState } from 'react';
import { clsx } from 'clsx';
import {
  Mic,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Quote,
  BarChart3,
  Loader2,
  Clock,
} from 'lucide-react';
import { Card, CardHeader } from '../common/Card';
import type {
  EarningsTranscriptsResponse,
  EarningsTranscript,
  EarningsTranscriptSummary,
} from '../../types';

interface EarningsTabProps {
  transcriptsData: EarningsTranscriptsResponse | null;
  loading: boolean;
}

// Verdict badge colors
export function VerdictBadge({ verdict, label }: { verdict: string; label: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold',
        verdict === 'beat' && 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
        verdict === 'miss' && 'bg-red-500/15 text-red-400 border border-red-500/30',
        verdict === 'inline' && 'bg-gray-500/15 text-gray-400 border border-gray-500/30'
      )}
    >
      {verdict === 'beat' && <TrendingUp className="h-3 w-3" />}
      {verdict === 'miss' && <TrendingDown className="h-3 w-3" />}
      {verdict === 'inline' && <Minus className="h-3 w-3" />}
      {label}: {verdict === 'beat' ? 'Beat' : verdict === 'miss' ? 'Miss' : 'Inline'}
    </span>
  );
}

export function SentimentIndicator({ sentiment }: { sentiment: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
        sentiment === 'positive' && 'bg-emerald-500/10 text-emerald-400',
        sentiment === 'negative' && 'bg-red-500/10 text-red-400',
        sentiment === 'neutral' && 'bg-gray-500/10 text-gray-400',
        sentiment === 'mixed' && 'bg-amber-500/10 text-amber-400'
      )}
    >
      {sentiment === 'positive' && <TrendingUp className="h-3 w-3" />}
      {sentiment === 'negative' && <TrendingDown className="h-3 w-3" />}
      {sentiment === 'neutral' && <Minus className="h-3 w-3" />}
      {sentiment === 'mixed' && <AlertTriangle className="h-3 w-3" />}
      {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
    </span>
  );
}

export function GuidanceBadge({ vs_expectations }: { vs_expectations: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
        vs_expectations === 'raised' && 'bg-emerald-500/10 text-emerald-400',
        vs_expectations === 'lowered' && 'bg-red-500/10 text-red-400',
        vs_expectations === 'maintained' && 'bg-blue-500/10 text-blue-400',
        vs_expectations === 'unclear' && 'bg-gray-500/10 text-gray-400'
      )}
    >
      {vs_expectations === 'raised' ? 'Raised' : vs_expectations === 'lowered' ? 'Lowered' : vs_expectations === 'maintained' ? 'Maintained' : 'Unclear'}
    </span>
  );
}

export function ToneBadge({ tone }: { tone: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        tone === 'confident' && 'bg-emerald-500/10 text-emerald-400',
        tone === 'optimistic' && 'bg-blue-500/10 text-blue-400',
        tone === 'cautious' && 'bg-amber-500/10 text-amber-400',
        tone === 'defensive' && 'bg-red-500/10 text-red-400',
        tone === 'neutral' && 'bg-gray-500/10 text-gray-400'
      )}
    >
      {tone.charAt(0).toUpperCase() + tone.slice(1)}
    </span>
  );
}

// Expandable quarter card for the history timeline
function QuarterCard({ transcript }: { transcript: EarningsTranscript }) {
  const [expanded, setExpanded] = useState(false);
  const summary = transcript.ai_summary as EarningsTranscriptSummary | null;

  if (!transcript.has_transcript || !summary) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] opacity-60">
        <Clock className="h-4 w-4 text-[var(--color-text-muted)]" />
        <span className="text-sm font-medium text-[var(--color-text-muted)]">
          Q{transcript.quarter} {transcript.year}
        </span>
        <span className="text-xs text-[var(--color-text-muted)] ml-auto">
          {transcript.has_transcript ? 'Summary pending...' : 'Data being collected...'}
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[var(--color-bg-tertiary)] transition-colors"
      >
        <span className="text-sm font-semibold text-[var(--color-text-primary)] min-w-[70px]">
          Q{transcript.quarter} {transcript.year}
        </span>

        {summary.beat_or_miss && (
          <VerdictBadge verdict={summary.beat_or_miss.verdict} label="EPS" />
        )}

        {summary.management_sentiment && (
          <ToneBadge tone={summary.management_sentiment.overall_tone} />
        )}

        <span className="text-xs text-[var(--color-text-muted)] ml-auto mr-2 hidden sm:block max-w-[200px] truncate">
          {summary.summary}
        </span>

        {expanded ? (
          <ChevronUp className="h-4 w-4 text-[var(--color-text-muted)] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[var(--color-text-muted)] flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-[var(--color-border)]">
          {/* Summary */}
          <p className="text-sm text-[var(--color-text-secondary)] pt-3">
            {summary.summary}
          </p>

          {/* Forward Guidance */}
          {summary.forward_guidance && (
            <div>
              <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5">
                Forward Guidance
              </h4>
              <div className="flex items-center gap-2 mb-1">
                <SentimentIndicator sentiment={summary.forward_guidance.sentiment} />
                <GuidanceBadge vs_expectations={summary.forward_guidance.vs_expectations} />
              </div>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {summary.forward_guidance.summary}
              </p>
            </div>
          )}

          {/* Key Topics */}
          {summary.key_topics && summary.key_topics.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5">
                Key Topics
              </h4>
              <div className="flex flex-wrap gap-2">
                {summary.key_topics.map((topic, i) => (
                  <span
                    key={i}
                    className={clsx(
                      'px-2.5 py-1 rounded-full text-xs font-medium',
                      topic.sentiment === 'positive' && 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
                      topic.sentiment === 'negative' && 'bg-red-500/10 text-red-400 border border-red-500/20',
                      topic.sentiment === 'neutral' && 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                    )}
                    title={topic.detail}
                  >
                    {topic.topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notable Quotes */}
          {summary.management_sentiment?.notable_quotes && summary.management_sentiment.notable_quotes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5">
                Notable Quotes
              </h4>
              <div className="space-y-2">
                {summary.management_sentiment.notable_quotes.map((quote, i) => (
                  <div key={i} className="flex gap-2 text-sm text-[var(--color-text-secondary)] italic">
                    <Quote className="h-4 w-4 text-[var(--color-text-muted)] flex-shrink-0 mt-0.5" />
                    <span>"{quote}"</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Disconnect Signal */}
          {summary.disconnect_signal?.detected && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400 uppercase">
                  Disconnect Signal (Strength: {summary.disconnect_signal.signal_strength}/100)
                </span>
              </div>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {summary.disconnect_signal.description}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EarningsTab({ transcriptsData, loading }: EarningsTabProps) {
  if (loading) {
    return (
      <Card className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-accent)] mb-4" />
        <p className="text-[var(--color-text-muted)]">Loading earnings data...</p>
      </Card>
    );
  }

  if (!transcriptsData || transcriptsData.transcripts.length === 0) {
    return (
      <Card className="text-center py-16">
        <Mic className="h-16 w-16 text-[var(--color-text-muted)] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">
          No Earnings Data Yet
        </h3>
        <p className="text-[var(--color-text-muted)] mt-2 max-w-md mx-auto">
          Earnings call transcripts are being collected in the background.
          Check back soon for AI-powered earnings analysis.
        </p>
      </Card>
    );
  }

  const transcripts = transcriptsData.transcripts;
  const withSummary = transcripts.filter(t => t.has_transcript && t.ai_summary);
  const latest = withSummary[0];
  const latestSummary = latest?.ai_summary as EarningsTranscriptSummary | null;

  // Build trend data from available summaries
  const trendData = withSummary
    .map(t => {
      const s = t.ai_summary as EarningsTranscriptSummary;
      return {
        label: `Q${t.quarter} ${t.year}`,
        confidence: s.management_sentiment?.confidence_level || 0,
        guidance: s.forward_guidance?.vs_expectations || 'unclear',
        verdict: s.beat_or_miss?.verdict || 'inline',
      };
    })
    .reverse(); // Oldest first for charts

  // Topic frequency across quarters
  const topicFreq = new Map<string, number>();
  withSummary.forEach(t => {
    const s = t.ai_summary as EarningsTranscriptSummary;
    s.key_topics?.forEach(topic => {
      topicFreq.set(topic.topic, (topicFreq.get(topic.topic) || 0) + 1);
    });
  });
  const recurringTopics = Array.from(topicFreq.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Coverage indicator */}
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
        <BarChart3 className="h-4 w-4" />
        <span>{transcriptsData.coverage}</span>
      </div>

      {/* Latest Earnings Summary Card */}
      {latest && latestSummary && (
        <Card>
          <CardHeader
            title={`Q${latest.quarter} ${latest.year} Earnings Call`}
            subtitle="Latest earnings call analysis"
            icon={<Mic className="h-5 w-5" />}
          />

          <div className="space-y-4">
            {/* Summary */}
            <p className="text-sm text-[var(--color-text-secondary)]">
              {latestSummary.summary}
            </p>

            {/* Beat/Miss Badges */}
            {latestSummary.beat_or_miss && (
              <div className="flex flex-wrap items-center gap-3">
                <VerdictBadge verdict={latestSummary.beat_or_miss.verdict} label="EPS" />
                {latestSummary.beat_or_miss.eps_actual != null && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    ${latestSummary.beat_or_miss.eps_actual?.toFixed(2)} actual vs ${latestSummary.beat_or_miss.eps_estimate?.toFixed(2)} est.
                  </span>
                )}
              </div>
            )}

            {/* Forward Guidance */}
            {latestSummary.forward_guidance && (
              <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)]">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                    Forward Guidance
                  </h4>
                  <SentimentIndicator sentiment={latestSummary.forward_guidance.sentiment} />
                  <GuidanceBadge vs_expectations={latestSummary.forward_guidance.vs_expectations} />
                </div>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {latestSummary.forward_guidance.summary}
                </p>
              </div>
            )}

            {/* Disconnect Signal */}
            {latestSummary.disconnect_signal?.detected && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  <h4 className="text-sm font-semibold text-amber-400">
                    Earnings Disconnect Detected
                  </h4>
                  <span className="text-xs text-amber-400/70">
                    Signal Strength: {latestSummary.disconnect_signal.signal_strength}/100
                  </span>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {latestSummary.disconnect_signal.description}
                </p>
              </div>
            )}

            {/* Key Topics */}
            {latestSummary.key_topics && latestSummary.key_topics.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[var(--color-text-muted)] mb-2">Key Topics</h4>
                <div className="flex flex-wrap gap-2">
                  {latestSummary.key_topics.map((topic, i) => (
                    <span
                      key={i}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-xs font-medium',
                        topic.sentiment === 'positive' && 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
                        topic.sentiment === 'negative' && 'bg-red-500/10 text-red-400 border border-red-500/20',
                        topic.sentiment === 'neutral' && 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                      )}
                      title={topic.detail}
                    >
                      {topic.topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Management Quotes */}
            {latestSummary.management_sentiment?.notable_quotes && latestSummary.management_sentiment.notable_quotes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-medium text-[var(--color-text-muted)]">Management Quotes</h4>
                  <ToneBadge tone={latestSummary.management_sentiment.overall_tone} />
                </div>
                <div className="space-y-2">
                  {latestSummary.management_sentiment.notable_quotes.map((quote, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-lg bg-[var(--color-bg-secondary)]">
                      <Quote className="h-4 w-4 text-[var(--color-accent)] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[var(--color-text-secondary)] italic">
                        "{quote}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Earnings Call History Timeline */}
      <Card>
        <CardHeader
          title="Earnings Call History"
          subtitle={`${withSummary.length} quarters with analysis`}
          icon={<BarChart3 className="h-5 w-5" />}
        />

        <div className="space-y-2">
          {transcripts.map((transcript) => (
            <QuarterCard key={`${transcript.year}-${transcript.quarter}`} transcript={transcript} />
          ))}
        </div>
      </Card>

      {/* Cross-Quarter Trends */}
      {trendData.length >= 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Management Confidence Trend */}
          <Card>
            <CardHeader
              title="Management Confidence"
              subtitle="Confidence level across quarters"
              icon={<TrendingUp className="h-5 w-5" />}
            />
            <div className="space-y-2">
              {trendData.map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[var(--color-text-muted)] min-w-[60px]">{d.label}</span>
                  <div className="flex-1 h-5 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        d.confidence >= 70 ? 'bg-emerald-500' : d.confidence >= 40 ? 'bg-amber-500' : 'bg-red-500'
                      )}
                      style={{ width: `${d.confidence}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-[var(--color-text-muted)] min-w-[30px] text-right">
                    {d.confidence}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Guidance Evolution + Beat/Miss Track Record */}
          <Card>
            <CardHeader
              title="Quarterly Track Record"
              subtitle="Beat/miss and guidance history"
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <div className="space-y-2">
              {trendData.map((d, i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <span className="text-xs text-[var(--color-text-muted)] min-w-[60px]">{d.label}</span>
                  <span
                    className={clsx(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium min-w-[50px] justify-center',
                      d.verdict === 'beat' && 'bg-emerald-500/15 text-emerald-400',
                      d.verdict === 'miss' && 'bg-red-500/15 text-red-400',
                      d.verdict === 'inline' && 'bg-gray-500/15 text-gray-400'
                    )}
                  >
                    {d.verdict === 'beat' ? 'Beat' : d.verdict === 'miss' ? 'Miss' : 'Inline'}
                  </span>
                  <GuidanceBadge vs_expectations={d.guidance} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Recurring Topics */}
      {recurringTopics.length > 0 && (
        <Card>
          <CardHeader
            title="Recurring Topics"
            subtitle="Most discussed topics across earnings calls"
            icon={<Mic className="h-5 w-5" />}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {recurringTopics.map(([topic, count]) => (
              <div
                key={topic}
                className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]"
              >
                <span className="text-sm text-[var(--color-text-primary)] truncate">{topic}</span>
                <span className="text-xs text-[var(--color-text-muted)] ml-2 flex-shrink-0">
                  {count}x
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
